import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';
import { NavLink } from '@/components/NavLink';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarFooter, useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, Tag, BarChart3,
  Users, MessageSquare, Settings, LogOut, ChevronRight, Building2,
  FileCheck, FolderCheck, UserCheck, Home, Heart, Eye,
  Briefcase, UserPlus, FolderKanban, HardHat, ClipboardList, UsersRound,
  Shield, Bell, Globe, PieChart, LandPlot, Store,
  Target, Megaphone, ListTodo, Flag, History, Copy, Mail, Star,
  DollarSign, Sparkles, SlidersHorizontal, LineChart, Inbox,
} from 'lucide-react';
import logo from '@/assets/logo.png';

type Item = { title: string; url: string; icon: any; arrow?: boolean; show: boolean };

export function DashboardSidebar() {
  const { hasRole, signOut } = useAuth();
  const { permissions: p } = useDashboardPermissions();
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const navigate = useNavigate();

  const renderItem = (item: Item) => (
    <SidebarMenuItem key={item.title + item.url}>
      <SidebarMenuButton asChild>
        <NavLink
          to={item.url}
          end={item.url === '/dashboard'}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[hsl(var(--dash-sidebar-foreground))] hover:bg-[hsl(var(--dash-sidebar-active))] hover:text-white transition-colors"
          activeClassName="!bg-[hsl(var(--dash-sidebar-active))] !text-white font-semibold shadow-md"
        >
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-sm">{item.title}</span>
              {item.arrow && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
            </>
          )}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  const renderSection = (label: string, items: Item[]) => {
    const visible = items.filter(i => i.show);
    if (visible.length === 0) return null;
    return (
      <SidebarGroup key={label}>
        {!collapsed && <SidebarGroupLabel className="text-[hsl(var(--dash-sidebar-foreground))]/60 text-[10px] uppercase tracking-wider px-5 mb-1">{label}</SidebarGroupLabel>}
        <SidebarGroupContent className="px-2">
          <SidebarMenu className="space-y-0.5">
            {visible.map(renderItem)}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  // ── Common items shown to ANY signed-in user ──
  const commonBottom: Item[] = [
    { title: 'Messages', url: '/dashboard/messages', icon: MessageSquare, show: p.canViewMessages },
    { title: 'Notifications', url: '/dashboard/notifications', icon: Bell, show: p.canViewNotifications },
    { title: 'Saved Properties', url: '/dashboard/saved', icon: Heart, show: p.canViewSavedProperties },
    { title: 'Settings', url: '/dashboard/profile', icon: Settings, show: true },
  ];

  const renderRoleSections = () => {
    // ── Superadmin (platform governance only — no operator features) ──
    if (hasRole('superadmin')) {
      const overview: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'Platform Analytics', url: '/dashboard/analytics', icon: PieChart, show: p.canViewAnalytics },
        { title: 'Funnel', url: '/dashboard/insights/funnel', icon: LineChart, show: p.canViewInsights },
        { title: 'Search Insights', url: '/dashboard/insights/search', icon: PieChart, show: p.canViewInsights },
        { title: 'Agency Scorecards', url: '/dashboard/insights/agencies', icon: Building2, show: p.canViewInsights },
      ];
      const moderation: Item[] = [
        { title: 'Property Approvals', url: '/dashboard/property-approvals', icon: Home, show: p.canApproveListings },
        { title: 'Plot Approvals', url: '/dashboard/plot-approvals', icon: LandPlot, show: p.canApproveListings },
        { title: 'Project Approvals', url: '/dashboard/project-approvals', icon: FolderCheck, show: p.canApproveListings },
        { title: 'Reviews Moderation', url: '/dashboard/reviews-moderation', icon: Star, show: true },
        { title: 'Reports & Abuse', url: '/dashboard/reports', icon: Flag, show: true },
        { title: 'Duplicates', url: '/dashboard/duplicates', icon: Copy, show: true },
      ];
      const accounts: Item[] = [
        { title: 'Agency Applications', url: '/dashboard/agency-applications', icon: Building2, show: p.canApproveApplications },
        { title: 'Dev Applications', url: '/dashboard/developer-applications', icon: FileCheck, show: p.canApproveApplications },
        { title: 'User Management', url: '/dashboard/users', icon: Users, arrow: true, show: p.canManageUsers },
        { title: 'Departments', url: '/dashboard/departments', icon: Building2, arrow: true, show: p.canManageDepartments },
        { title: 'Internal Teams', url: '/dashboard/internal-teams', icon: UsersRound, show: p.canManageInternalTeam },
      ];
      const monetization: Item[] = [
        { title: 'Featured / Boost', url: '/dashboard/featured', icon: Sparkles, show: true },
        { title: 'Revenue & Subs', url: '/dashboard/revenue', icon: DollarSign, show: true },
      ];
      const communication: Item[] = [
        { title: 'Landing CMS', url: '/dashboard/cms', icon: FileCheck, show: true },
        { title: 'Broadcasts', url: '/dashboard/broadcasts', icon: Megaphone, show: true },
        { title: 'Email Templates', url: '/dashboard/email-templates', icon: Mail, show: true },
        { title: 'Notifications', url: '/dashboard/notifications', icon: Bell, show: true },
      ];
      const system: Item[] = [
        { title: 'Audit Log', url: '/dashboard/audit-log', icon: History, show: true },
        { title: 'System Settings', url: '/dashboard/system-settings', icon: SlidersHorizontal, show: true },
        { title: 'Profile', url: '/dashboard/profile', icon: Settings, show: true },
      ];
      return (
        <>
          {renderSection('Overview', overview)}
          {renderSection('Content Moderation', moderation)}
          {renderSection('Accounts & Onboarding', accounts)}
          {renderSection('Monetization', monetization)}
          {renderSection('Communication', communication)}
          {renderSection('Trust & System', system)}
        </>
      );
    }

    // ── Internal: Sales Manager ──
    if (hasRole('sales_manager')) {
      const main: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'All Teams', url: '/dashboard/sales-teams', icon: Users, show: p.canManageInternalTeam },
        { title: 'All Prospects', url: '/dashboard/sales-prospects', icon: Target, show: p.canManageProspects },
      ];
      const approvals: Item[] = [
        { title: 'Agency Applications', url: '/dashboard/agency-applications', icon: Building2, show: p.canApproveApplications },
        { title: 'Developer Applications', url: '/dashboard/developer-applications', icon: FileCheck, show: p.canApproveApplications },
      ];
      const mgmt: Item[] = [
        { title: 'Team Setup', url: '/dashboard/internal-team-setup', icon: UsersRound, show: p.canManageInternalTeam },
        { title: 'Analytics', url: '/dashboard/sales-analytics', icon: BarChart3, show: p.canViewDepartmentAnalytics },
        ...commonBottom,
      ];
      return <>{renderSection('Sales', main)}{renderSection('Onboarding Approvals', approvals)}{renderSection('Management', mgmt)}</>;
    }

    // ── Internal: Sales Team Leader ──
    if (hasRole('sales_team_leader')) {
      const main: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'Team Tasks', url: '/dashboard/sales-tasks', icon: ListTodo, show: p.canViewInternalTasks },
        { title: 'Team Prospects', url: '/dashboard/sales-prospects', icon: Target, show: p.canManageProspects },
      ];
      const approvals: Item[] = [
        { title: 'Agency Applications', url: '/dashboard/agency-applications', icon: Building2, show: p.canApproveApplications },
        { title: 'Developer Applications', url: '/dashboard/developer-applications', icon: FileCheck, show: p.canApproveApplications },
      ];
      const team: Item[] = [
        { title: 'Team Members', url: '/dashboard/sales-team', icon: Users, show: true },
        ...commonBottom,
      ];
      return <>{renderSection('Sales', main)}{renderSection('Onboarding Approvals', approvals)}{renderSection('Team', team)}</>;
    }

    // ── Internal: Sales Agent ──
    if (hasRole('sales_agent')) {
      const items: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'My Tasks', url: '/dashboard/sales-tasks', icon: ListTodo, show: p.canViewInternalTasks },
        { title: 'My Prospects', url: '/dashboard/sales-prospects', icon: Target, show: p.canManageProspects },
      ];
      const approvals: Item[] = [
        { title: 'Agency Applications', url: '/dashboard/agency-applications', icon: Building2, show: p.canApproveApplications },
        { title: 'Developer Applications', url: '/dashboard/developer-applications', icon: FileCheck, show: p.canApproveApplications },
      ];
      return <>{renderSection('Sales', items)}{renderSection('Onboarding Approvals', approvals)}{renderSection('More', commonBottom)}</>;
    }

    // ── Internal: Marketing Manager ──
    if (hasRole('marketing_manager')) {
      const main: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'All Teams', url: '/dashboard/marketing-teams', icon: Users, show: p.canManageInternalTeam },
        { title: 'All Campaigns', url: '/dashboard/marketing-campaigns', icon: Megaphone, show: p.canManageCampaigns },
      ];
      const mgmt: Item[] = [
        { title: 'Team Setup', url: '/dashboard/internal-team-setup', icon: UsersRound, show: p.canManageInternalTeam },
        { title: 'Analytics', url: '/dashboard/marketing-analytics', icon: BarChart3, show: p.canViewDepartmentAnalytics },
        ...commonBottom,
      ];
      return <>{renderSection('Marketing', main)}{renderSection('Management', mgmt)}</>;
    }

    // ── Internal: Marketing Team Leader ──
    if (hasRole('marketing_team_leader')) {
      const main: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'Team Tasks', url: '/dashboard/marketing-tasks', icon: ListTodo, show: p.canViewInternalTasks },
        { title: 'Campaigns', url: '/dashboard/marketing-campaigns', icon: Megaphone, show: p.canManageCampaigns },
      ];
      const team: Item[] = [
        { title: 'Team Members', url: '/dashboard/marketing-team', icon: Users, show: true },
        ...commonBottom,
      ];
      return <>{renderSection('Marketing', main)}{renderSection('Team', team)}</>;
    }

    // ── Internal: Marketing Agent ──
    if (hasRole('marketing_agent')) {
      const items: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'My Tasks', url: '/dashboard/marketing-tasks', icon: ListTodo, show: p.canViewInternalTasks },
        { title: 'My Campaigns', url: '/dashboard/marketing-campaigns', icon: Megaphone, show: p.canManageCampaigns },
        { title: 'Landing CMS', url: '/dashboard/cms', icon: FileCheck, show: true },
        ...commonBottom,
      ];
      return renderSection('Marketing', items);
    }

    // ── Internal: IT / Accounting / HR ──
    if (hasRole('it_staff') || hasRole('accounting_staff') || hasRole('hr_staff')) {
      const taskUrl = hasRole('it_staff') ? '/dashboard/it-tasks'
        : hasRole('accounting_staff') ? '/dashboard/accounting-tasks'
        : '/dashboard/hr-tasks';
      const label = hasRole('it_staff') ? 'IT Department'
        : hasRole('accounting_staff') ? 'Accounting'
        : 'Human Resource';
      const items: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'My Tasks', url: taskUrl, icon: ListTodo, show: p.canViewInternalTasks },
        ...commonBottom,
      ];
      return renderSection(label, items);
    }

    // ── Quality Control team ──
    if (hasRole('quality_control_lead') || hasRole('quality_control_agent')) {
      const isLead = hasRole('quality_control_lead');
      const items: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'Audit Queue', url: '/dashboard/audit-queue', icon: FileCheck, show: p.canAuditListings },
      ];
      const approvals: Item[] = [
        { title: 'Property Approvals', url: '/dashboard/property-approvals', icon: Home, show: p.canApproveListings },
        { title: 'Plot Approvals', url: '/dashboard/plot-approvals', icon: LandPlot, show: p.canApproveListings },
        { title: 'Project Approvals', url: '/dashboard/project-approvals', icon: FolderCheck, show: p.canApproveListings },
      ];
      const team: Item[] = [
        ...(isLead ? [{ title: 'Team Performance', url: '/dashboard/qc-team', icon: Users, show: true }] : []),
        ...commonBottom,
      ];
      return <>{renderSection('Quality Control', items)}{renderSection('Listing Approvals', approvals)}{renderSection('More', team)}</>;
    }

    // ── Ad Operations team ──
    if (hasRole('ad_operations_lead') || hasRole('ad_operations_agent')) {
      const items: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'Featured Listings', url: '/dashboard/featured-listings', icon: Megaphone, show: p.canManageFeatured },
        { title: 'Ad Campaigns', url: '/dashboard/ad-campaigns', icon: Megaphone, show: p.canManageAdCampaigns },
        ...commonBottom,
      ];
      return renderSection('Ad Operations', items);
    }

    // ── Developer ──
    if (hasRole('developer')) {
      const main: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'My Projects', url: '/dashboard/my-projects', icon: FolderKanban, arrow: true, show: p.canViewMyProjects },
        { title: 'Add Project', url: '/dashboard/projects/new', icon: Building2, show: p.canAddProject },
      ];
      const inventory: Item[] = [
        { title: 'Bookings', url: '/dashboard/bookings', icon: ClipboardList, show: p.canManageBookings },
        { title: 'Plot Map', url: '/dashboard/plot-map', icon: Globe, show: p.canManageBookings },
        { title: 'Transfers', url: '/dashboard/transfers', icon: Briefcase, show: p.canManageBookings },
        { title: 'Inventory', url: '/dashboard/inventory', icon: HardHat, show: p.canManageBookings },
      ];
      const other: Item[] = [
        { title: 'Project Leads', url: '/dashboard/leads', icon: MessageSquare, show: p.canViewLeads },
        { title: 'Analytics', url: '/dashboard/analytics', icon: BarChart3, show: p.canViewAnalytics },
        ...commonBottom,
      ];
      return (
        <>
          {renderSection('Main Menu', main)}
          {renderSection('Inventory', inventory)}
          {renderSection('Management', other)}
        </>
      );
    }

    // ── Agency Admin ──
    if (hasRole('agency_admin')) {
      const main: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'Agency Listings', url: '/dashboard/listings', icon: Home, arrow: true, show: p.canViewMyListings },
        { title: 'Add Property', url: '/dashboard/properties/new', icon: Tag, show: p.canAddProperty },
        { title: 'My Plots', url: '/dashboard/plots', icon: LandPlot, arrow: true, show: p.canViewMyListings },
        { title: 'Add Plot', url: '/dashboard/plots/new', icon: LandPlot, show: p.canAddPlot },
        { title: 'Add Commercial', url: '/dashboard/commercial/new', icon: Store, show: p.canAddCommercial },
        { title: 'Add Project', url: '/dashboard/projects/new', icon: Building2, show: p.canAddProject },
        { title: 'My Leads', url: '/dashboard/leads', icon: MessageSquare, show: p.canViewLeads },
      ];
      const pipeline: Item[] = [
        { title: 'Lead Inbox (All)', url: '/dashboard/agency-inbox', icon: Inbox, show: p.canManageAgencyMembers },
        { title: 'Lead Pipeline', url: '/dashboard/pipeline', icon: FolderKanban, show: p.canViewLeadPipeline },
        { title: 'Clients', url: '/dashboard/clients', icon: Users, show: p.canManageClients },
        { title: 'Lead Assignment', url: '/dashboard/lead-assignment', icon: UserPlus, show: p.canAssignLeads },
      ];
      const team: Item[] = [
        { title: 'Team Members', url: '/dashboard/members', icon: UsersRound, arrow: true, show: p.canManageAgencyMembers },
        { title: 'Routing Settings', url: '/dashboard/agency-settings', icon: SlidersHorizontal, show: p.canManageAgencyMembers },
        { title: 'Analytics', url: '/dashboard/analytics', icon: BarChart3, show: p.canViewAnalytics },
        { title: 'Reviews', url: '/dashboard/reviews', icon: Eye, show: p.canViewReviews },
        ...commonBottom,
      ];
      return (
        <>
          {renderSection('Main Menu', main)}
          {renderSection('Pipeline', pipeline)}
          {renderSection('Agency', team)}
        </>
      );
    }

    // ── Agent ──
    if (hasRole('agent')) {
      const main: Item[] = [
        { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
        { title: 'My Listings', url: '/dashboard/listings', icon: Home, arrow: true, show: p.canViewMyListings },
        { title: 'Agency Listings', url: '/dashboard/listings?scope=agency', icon: Building2, show: p.canViewAgencyListings },
        { title: 'Add Property', url: '/dashboard/properties/new', icon: Tag, show: p.canAddProperty },
        { title: 'My Plots', url: '/dashboard/plots', icon: LandPlot, arrow: true, show: p.canViewMyListings },
        { title: 'Add Plot', url: '/dashboard/plots/new', icon: LandPlot, show: p.canAddPlot },
        { title: 'Add Commercial', url: '/dashboard/commercial/new', icon: Store, show: p.canAddCommercial },
        { title: 'Add Project', url: '/dashboard/projects/new', icon: Building2, show: p.canAddProject },
        { title: 'My Leads', url: '/dashboard/leads', icon: MessageSquare, show: p.canViewLeads },
      ];
      const pipeline: Item[] = [
        { title: 'Lead Pipeline', url: '/dashboard/pipeline', icon: FolderKanban, show: p.canViewLeadPipeline },
        { title: 'Clients', url: '/dashboard/clients', icon: Users, show: p.canManageClients },
      ];
      const other: Item[] = [
        { title: 'Analytics', url: '/dashboard/analytics', icon: BarChart3, show: p.canViewAnalytics },
        { title: 'Reviews', url: '/dashboard/reviews', icon: Eye, show: p.canViewReviews },
        ...commonBottom,
      ];
      return (
        <>
          {renderSection('Main Menu', main)}
          {renderSection('Pipeline', pipeline)}
          {renderSection('Others', other)}
        </>
      );
    }

    // ── Default user ──
    const items: Item[] = [
      { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, show: true },
      ...commonBottom,
    ];
    return renderSection('Menu', items);
  };

  const getRoleLabel = () => {
    if (hasRole('superadmin')) return 'Super Admin Panel';
    if (hasRole('sales_manager')) return 'Sales Manager';
    if (hasRole('sales_team_leader')) return 'Sales Team Leader';
    if (hasRole('sales_agent')) return 'Sales Agent';
    if (hasRole('marketing_manager')) return 'Marketing Manager';
    if (hasRole('marketing_team_leader')) return 'Marketing Team Leader';
    if (hasRole('marketing_agent')) return 'Marketing Member';
    if (hasRole('it_staff')) return 'IT Staff';
    if (hasRole('accounting_staff')) return 'Accounting Staff';
    if (hasRole('hr_staff')) return 'HR Staff';
    if (hasRole('quality_control_lead')) return 'QC Team Lead';
    if (hasRole('quality_control_agent')) return 'QC Auditor';
    if (hasRole('ad_operations_lead')) return 'Ad Ops Lead';
    if (hasRole('ad_operations_agent')) return 'Ad Ops Agent';
    if (hasRole('developer')) return 'Developer Panel';
    if (hasRole('agency_admin')) return 'Agency Panel';
    if (hasRole('agent')) return 'Agent Panel';
    return 'My Account';
  };

  return (
    <Sidebar collapsible="icon" className="border-0">
      <div className="flex flex-col h-full bg-[hsl(var(--dash-sidebar))]">
        <SidebarContent className="bg-transparent">
          <div className="p-5 pb-3">
            <button onClick={() => navigate('/')} className="flex items-center gap-2.5">
              <img src={logo} alt="Home" className="h-9 object-contain" />
              {!collapsed && (
                <div>
                  <span className="font-display font-bold text-white text-sm block leading-tight">EthioRealEstates</span>
                  <span className="text-[10px] text-[hsl(var(--dash-sidebar-foreground))]">{getRoleLabel()}</span>
                </div>
              )}
            </button>
          </div>

          {renderRoleSections()}
        </SidebarContent>

        <SidebarFooter className="bg-transparent mt-auto">
          {!collapsed && (
            <div className="px-5 pb-2 text-[hsl(var(--dash-sidebar-foreground))]/60">
              <div className="text-[11px] font-semibold text-[hsl(var(--dash-sidebar-foreground))]">EthioRealEstates</div>
              <div className="text-[10px]">© {new Date().getFullYear()} All Rights Reserved</div>
            </div>
          )}
          <div className="px-2 pb-3">
            <Button
              variant="ghost" size="sm"
              className="w-full justify-start text-[hsl(var(--dash-sidebar-foreground))]/70 hover:text-white hover:bg-white/10"
              onClick={() => { signOut(); navigate('/'); }}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {!collapsed && 'Sign Out'}
            </Button>
          </div>
        </SidebarFooter>
      </div>
    </Sidebar>
  );
}
